// Network monitoring utility to capture raw HTTP responses
// This helps debug what the server is actually returning before AWS Amplify processes it

import { apiLogger } from './logger';

// Global storage for network responses that we can check later
let lastCognitoError: any = null;
let lastCognitoErrorTimestamp: number = 0;

export function setupNetworkMonitoring() {
  // Only run in development
  if (import.meta.env.PROD) return;

  apiLogger.debug('🔍 NetworkMonitor: Setting up network monitoring...');

  // Intercept fetch requests
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const [url, options] = args;
    
    apiLogger.debug('🔍 NetworkMonitor: Fetch request:', {
      url: typeof url === 'string' ? url : url.toString(),
      method: options?.method || 'GET',
      headers: options?.headers,
      body: options?.body
    });

    try {
      const response = await originalFetch.apply(this, args);
      
      // Clone the response so we can read it without consuming it
      const responseClone = response.clone();
      
      // Log response details
      apiLogger.debug('🔍 NetworkMonitor: Fetch response:', {
        url: typeof url === 'string' ? url : url.toString(),
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        ok: response.ok
      });

      // Try to read the response body
      try {
        const responseText = await responseClone.text();
        apiLogger.debug('🔍 NetworkMonitor: Response body:', responseText);
        
        // Try to parse as JSON if possible
        if (responseText) {
          try {
            const responseJson = JSON.parse(responseText);
            apiLogger.debug('🔍 NetworkMonitor: Response JSON:', responseJson);
            
            // Check if this is a Cognito error response
            const urlString = typeof url === 'string' ? url : url.toString();
            if (urlString.includes('cognito-idp') && responseJson.__type) {
              apiLogger.debug('🔍 NetworkMonitor: Storing Cognito error for later reference:', responseJson);
              lastCognitoError = responseJson;
              lastCognitoErrorTimestamp = Date.now();
              
              // Make it available globally for error checking
              (window as any).lastCognitoError = responseJson;
              (window as any).lastCognitoErrorTimestamp = lastCognitoErrorTimestamp;
            }
          } catch (jsonError) {
            apiLogger.debug('🔍 NetworkMonitor: Response is not JSON');
          }
        }
      } catch (bodyError) {
        apiLogger.debug('🔍 NetworkMonitor: Could not read response body:', bodyError);
      }

      return response;
    } catch (error) {
      apiLogger.error('🔍 NetworkMonitor: Fetch error:', error);
      throw error;
    }
  };

  // Intercept XMLHttpRequest
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    apiLogger.debug('🔍 NetworkMonitor: XHR request:', { method, url });
    
    // Store request details
    (this as any)._requestDetails = { method, url };
    
    return originalXHROpen.call(this, method, url, ...args);
  };

  XMLHttpRequest.prototype.send = function(body) {
    const requestDetails = (this as any)._requestDetails;
    
    apiLogger.debug('🔍 NetworkMonitor: XHR send:', {
      ...requestDetails,
      body: body
    });

    // Add event listeners to capture response
    this.addEventListener('load', function() {
      apiLogger.debug('🔍 NetworkMonitor: XHR response:', {
        url: requestDetails?.url,
        status: this.status,
        statusText: this.statusText,
        responseText: this.responseText,
        responseHeaders: this.getAllResponseHeaders()
      });

      // Try to parse response as JSON
      if (this.responseText) {
        try {
          const responseJson = JSON.parse(this.responseText);
          apiLogger.debug('🔍 NetworkMonitor: XHR Response JSON:', responseJson);
          
          // Check if this is a Cognito error response
          if (requestDetails?.url?.includes('cognito-idp') && responseJson.__type) {
            apiLogger.debug('🔍 NetworkMonitor: Storing Cognito XHR error for later reference:', responseJson);
            lastCognitoError = responseJson;
            lastCognitoErrorTimestamp = Date.now();
            
            // Make it available globally for error checking
            (window as any).lastCognitoError = responseJson;
            (window as any).lastCognitoErrorTimestamp = lastCognitoErrorTimestamp;
          }
        } catch (jsonError) {
          apiLogger.debug('🔍 NetworkMonitor: XHR Response is not JSON');
        }
      }
    });

    this.addEventListener('error', function() {
      apiLogger.error('🔍 NetworkMonitor: XHR error:', {
        url: requestDetails?.url,
        status: this.status,
        statusText: this.statusText,
        responseText: this.responseText
      });
    });

    return originalXHRSend.call(this, body);
  };

  apiLogger.debug('🔍 NetworkMonitor: Network monitoring setup complete');
}

// Function to check if there was a recent UserNotConfirmedException
export function checkForRecentUserNotConfirmedException(withinMs: number = 5000): any | null {
  const now = Date.now();
  if (lastCognitoError && 
      lastCognitoErrorTimestamp && 
      (now - lastCognitoErrorTimestamp) <= withinMs &&
      lastCognitoError.__type === 'UserNotConfirmedException') {
    apiLogger.debug('🔍 NetworkMonitor: Found recent UserNotConfirmedException:', lastCognitoError);
    return lastCognitoError;
  }
  return null;
}

// Function to manually inspect the last network error
export function inspectLastNetworkError() {
  apiLogger.debug('🔍 NetworkMonitor: Inspecting last network activity...');
  apiLogger.debug('🔍 NetworkMonitor: Last Cognito error:', lastCognitoError);
  apiLogger.debug('🔍 NetworkMonitor: Last error timestamp:', lastCognitoErrorTimestamp);
  apiLogger.debug('🔍 NetworkMonitor: Time since last error:', Date.now() - lastCognitoErrorTimestamp, 'ms');
  
  return {
    lastError: lastCognitoError,
    timestamp: lastCognitoErrorTimestamp,
    timeSince: Date.now() - lastCognitoErrorTimestamp,
    message: 'Check the console logs above for detailed network activity',
    tip: 'Look for "NetworkMonitor" logs to see raw server responses'
  };
}

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).inspectLastNetworkError = inspectLastNetworkError;
  (window as any).checkForRecentUserNotConfirmedException = checkForRecentUserNotConfirmedException;
}
